import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Button } from '@liminal/web-ui/primitives/Button';
import { PageHeader } from '@liminal/web-ui/display/PageHeader';
import { Input } from '@liminal/web-ui/forms/Input';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { ErrorState } from '@liminal/web-ui/feedback/ErrorState';
import { LoadingSpinner } from '@liminal/web-ui/feedback/LoadingSpinner';
import { isHttpError } from '@liminal/errors/http-errors';
import type { RewardBudget } from '@/api/types';
import { getErrorMessage } from '@/errors/messages';
import { useCreateRewardBudget, useRewardBudget, useUpdateRewardBudget } from '@/hooks/useRewards';
import { fromLocalInput, parseDecimal, resourceId, toLocalInput } from '@/lib/rewards';

/** The fields an update may name. */
const BUDGET_PATHS = ['display_name', 'limit_usd', 'max_payment_usd', 'start_time', 'end_time'] as const;

interface FormState {
  readonly displayName: string;
  readonly limitUsd: string;
  readonly maxPaymentUsd: string;
  readonly start: string;
  readonly end: string;
}

function toFormState(b: RewardBudget | undefined): FormState {
  return {
    displayName: b?.displayName ?? '',
    limitUsd: b?.limitUsd ?? '',
    maxPaymentUsd: b?.maxPaymentUsd ?? '',
    start: toLocalInput(b?.startTime),
    end: toLocalInput(b?.endTime),
  };
}

/** Why the form cannot be sent yet, or null when it can. */
function validate(form: FormState): string | null {
  if (form.displayName.trim() === '') return 'Give the budget a name.';
  if (!parseDecimal(form.limitUsd)) return 'The total must be a USD amount, such as 10000.';
  if (!parseDecimal(form.maxPaymentUsd)) return 'The cap per payment must be a USD amount, such as 100.';
  return null;
}

export function BudgetFormPage() {
  const { id } = useParams<{ id: string }>();
  const budgetId = id ?? '';
  const budget = useRewardBudget(budgetId);

  if (budgetId === '') return <BudgetForm />;
  if (budget.isLoading) return <LoadingSpinner />;
  if (budget.isError || !budget.data) {
    return <ErrorState message={getErrorMessage(budget.error)} onRetry={() => void budget.refetch()} />;
  }
  return <BudgetForm key={budget.data.etag} current={budget.data} />;
}

function BudgetForm({ current }: { readonly current?: RewardBudget }) {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(() => toFormState(current));
  // One idempotency key per visit, kept across a retry, so a resubmit after a
  // timeout replays the save rather than making a second one.
  const [requestId] = useState(() => crypto.randomUUID());
  const [error, setError] = useState('');
  const create = useCreateRewardBudget();
  const update = useUpdateRewardBudget();
  const saving = create.isPending || update.isPending;
  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  const handleSave = async () => {
    const invalid = validate(form);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError('');
    const budget: Omit<RewardBudget, 'name'> = {
      displayName: form.displayName.trim(),
      limitUsd: form.limitUsd.trim(),
      maxPaymentUsd: form.maxPaymentUsd.trim(),
      startTime: fromLocalInput(form.start),
      endTime: fromLocalInput(form.end),
    };
    try {
      if (!current) {
        const created = await create.mutateAsync({ budget, requestId });
        navigate(`/rewards/budgets/${resourceId(created.name, 'rewardBudgets')}`);
        return;
      }
      const original = toFormState(current);
      const changed = {
        display_name: form.displayName.trim() !== original.displayName,
        limit_usd: form.limitUsd.trim() !== original.limitUsd,
        max_payment_usd: form.maxPaymentUsd.trim() !== original.maxPaymentUsd,
        start_time: form.start !== original.start,
        end_time: form.end !== original.end,
      };
      const updateMask = BUDGET_PATHS.filter((path) => changed[path]);
      if (updateMask.length === 0) {
        setError('Nothing has changed.');
        return;
      }
      const id = resourceId(current.name, 'rewardBudgets');
      await update.mutateAsync({ id, budget: { ...budget, name: current.name, etag: current.etag }, updateMask, requestId });
      navigate(`/rewards/budgets/${id}`);
    } catch (e) {
      setError(
        isHttpError(e) && e.status === 409
          ? 'This budget was changed by someone else. Reload it and make your change again.'
          : getErrorMessage(e),
      );
    }
  };

  const backTo = current ? `/rewards/budgets/${resourceId(current.name, 'rewardBudgets')}` : '/rewards/budgets';

  return (
    <Stack spacing={3}>
      <Box>
        <Button variant="link" size="sm" startIcon="arrow-back" onClick={() => navigate(backTo)} disabled={saving}>
          {current ? (current.displayName ?? 'Budget') : 'Budgets'}
        </Button>
      </Box>
      <PageHeader
        title={current ? `Edit ${current.displayName ?? 'budget'}` : 'New budget'}
        autoTitleCase={false}
        subtitle="A budget caps how much Pay with LIL may pay back. Partners draw from it."
      />
      <Card variant="elevated" sx={{ maxWidth: 760 }}>
        <Stack spacing={3}>
          <Input
            label="Name"
            value={form.displayName}
            onChange={(displayName) => set({ displayName })}
            helper="Only staff see it, such as Launch partners Q4"
            required
          />

          <Stack spacing={2}>
            <Typography variant="subtitle1">Money</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Input
                label="Total (USD)"
                value={form.limitUsd}
                onChange={(limitUsd) => set({ limitUsd })}
                inputMode="decimal"
                helper="The most it will ever pay back"
                required
              />
              <Input
                label="Cap per payment (USD)"
                value={form.maxPaymentUsd}
                onChange={(maxPaymentUsd) => set({ maxPaymentUsd })}
                inputMode="decimal"
                helper="A bigger payment cannot be paid with LIL"
                required
              />
            </Stack>
          </Stack>

          <Stack spacing={2}>
            <Typography variant="subtitle1">When it runs</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Input
                label="Starts"
                type="datetime-local"
                value={form.start}
                onChange={(start) => set({ start })}
                helper="Your local time. Empty: starts now"
              />
              <Input
                label="Ends"
                type="datetime-local"
                value={form.end}
                onChange={(end) => set({ end })}
                helper="Your local time. Empty: runs until closed"
              />
            </Stack>
          </Stack>

          <FormErrorBanner error={error} />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="link" onClick={() => navigate(backTo)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} loading={saving}>
              {current ? 'Save changes' : 'Add budget'}
            </Button>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
